import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Package,
  Coffee,
  Activity,
  Heart,
  Umbrella,
  Users,
  Gift,
  DollarSign,
  Music,
  Image,
  Dog,
  Smile,
  Flower,
  Ship,
  Feather,
  Printer,
  Utensils,
  Disc,
  Plane,
  Minus,
  Film,
  Book,
  Star,
  Info,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  Zap,
  TrendingUp,
  Construction,
  Award,
  Crown,
  BadgeDollarSign,
  Sparkles,
  Fish,
} from "lucide-react";

// Final optimized FacilityManagementTab with complete memory leak fix
const FacilityManagementTab = ({
  selectedTab,
  facilityTypes,
  buildFacility,
  upgradeFacility,
  money,
  staff,
  facilities,
  renderStars,
  guests,
  reputation,
}) => {
  // State management
  const [showAdvancedStats, setShowAdvancedStats] = useState(false);
  const [filteredFacilityTypes, setFilteredFacilityTypes] = useState("all");
  const [sortOrder, setSortOrder] = useState("default");
  const [recentlyBuilt, setRecentlyBuilt] = useState([]);

  // Refs for timeout tracking to prevent memory leaks
  const animationTimerRef = useRef(null);
  const chunkTimerRef = useRef(null);

  // State for chunked rendering
  const [visibleFacilities, setVisibleFacilities] = useState([]);
  const [isChunking, setIsChunking] = useState(false);
  const [chunksLoaded, setChunksLoaded] = useState(0);

  // Cache to prevent redundant calculations
  const calculationCacheRef = useRef({});

  // Normalized facility types to ensure consistent structure
  const normalizedFacilityTypes = useMemo(() => {
    return facilityTypes.map((facility) => {
      // Ensure all facilities have consistent properties
      return {
        type: facility.type || "Unknown",
        baseCost: facility.baseCost || 10000,
        incomePerGuest: facility.incomePerGuest || 0,
        upkeep: facility.upkeep || 100,
        description: facility.description || "No description available",
        // Make sure all facilities have an icon
        icon: facility.icon || <Package className="w-4 h-4" />,
        // Add any missing properties with defaults
        specialFeature: facility.specialFeature || null,
        requiredStaff: facility.requiredStaff || [],
      };
    });
  }, [facilityTypes]);

  // Lookup table to avoid repeated facility searches
  const facilityTypesMap = useMemo(() => {
    const map = {};
    normalizedFacilityTypes.forEach((facility) => {
      map[facility.type] = facility;
    });
    return map;
  }, [normalizedFacilityTypes]);

  // Clear animation effects after 3 seconds
  useEffect(() => {
    // Clear any existing timeout to prevent memory leaks
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }

    // Set new timeout
    animationTimerRef.current = setTimeout(() => {
      setRecentlyBuilt([]);
      animationTimerRef.current = null;
    }, 3000);

    // Cleanup on unmount or before next effect run
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, [recentlyBuilt]);

  // Reset calculation cache when relevant dependencies change
  useEffect(() => {
    calculationCacheRef.current = {};
  }, [guests.length, facilities.length, reputation]);

  // Improved chunking implementation for processing facility list
  useEffect(() => {
    // Skip processing if not on facilities tab
    if (selectedTab !== "facilities") {
      setVisibleFacilities([]);
      return;
    }

    // Clear existing chunk timer if there is one
    if (chunkTimerRef.current) {
      clearTimeout(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }

    // Start the chunking process
    setIsChunking(true);
    setChunksLoaded(0);

    // Get the filtered and sorted facilities
    const allFacilitiesToDisplay = getFilteredAndSortedFacilities();
    const totalCount = allFacilitiesToDisplay.length;

    // For small lists, render all at once
    if (totalCount <= 10) {
      setVisibleFacilities(allFacilitiesToDisplay);
      setIsChunking(false);
      setChunksLoaded(totalCount);
      return;
    }

    // Initialize with the first chunk
    const CHUNK_SIZE = 5;
    setVisibleFacilities(allFacilitiesToDisplay.slice(0, CHUNK_SIZE));
    setChunksLoaded(CHUNK_SIZE);

    // Function to process the next chunk
    const processNextChunk = (currentIndex) => {
      // If we've processed all items, stop chunking
      if (currentIndex >= totalCount) {
        setIsChunking(false);
        return;
      }

      // Calculate next chunk boundaries
      const nextIndex = Math.min(currentIndex + CHUNK_SIZE, totalCount);

      // Update visible facilities by appending the next chunk
      setVisibleFacilities((prev) => {
        // Safety check to ensure we're not duplicating items
        const existingIds = new Set(prev.map((f) => f.type));
        const newItems = allFacilitiesToDisplay
          .slice(currentIndex, nextIndex)
          .filter((f) => !existingIds.has(f.type));

        return [...prev, ...newItems];
      });

      // Update progress
      setChunksLoaded(nextIndex);

      // Schedule next chunk with a small delay
      chunkTimerRef.current = setTimeout(() => {
        processNextChunk(nextIndex);
      }, 10);
    };

    // Start processing with a slight delay
    chunkTimerRef.current = setTimeout(() => {
      processNextChunk(CHUNK_SIZE);
    }, 50);

    // Cleanup function
    return () => {
      if (chunkTimerRef.current) {
        clearTimeout(chunkTimerRef.current);
        chunkTimerRef.current = null;
      }
    };
  }, [
    selectedTab,
    filteredFacilityTypes,
    sortOrder,
    facilities,
    normalizedFacilityTypes,
  ]);

  // Cleanup all timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);
    };
  }, []);

  // Get filtered and sorted facilities (memoized to prevent recalculation)
  const getFilteredAndSortedFacilities = useCallback(() => {
    // Skip processing if no facilities or not on facilities tab
    if (selectedTab !== "facilities" || !normalizedFacilityTypes.length) {
      return [];
    }

    // Apply filters
    let filtered = [...normalizedFacilityTypes];

    if (filteredFacilityTypes === "built") {
      filtered = filtered.filter((type) =>
        facilities.some((f) => f.name === type.type)
      );
    } else if (filteredFacilityTypes === "unbuilt") {
      filtered = filtered.filter(
        (type) => !facilities.some((f) => f.name === type.type)
      );
    } else if (filteredFacilityTypes === "affordable") {
      filtered = filtered.filter((type) => {
        const existingFacility = facilities.find((f) => f.name === type.type);
        if (existingFacility) {
          return (
            money >= 5000 * existingFacility.level && existingFacility.level < 5
          );
        } else {
          return money >= type.baseCost;
        }
      });
    }

    // Apply sorting
    let sorted;
    switch (sortOrder) {
      case "cost-asc":
        sorted = [...filtered].sort((a, b) => a.baseCost - b.baseCost);
        break;
      case "cost-desc":
        sorted = [...filtered].sort((a, b) => b.baseCost - a.baseCost);
        break;
      case "income-asc":
        sorted = [...filtered].sort(
          (a, b) => a.incomePerGuest - b.incomePerGuest
        );
        break;
      case "income-desc":
        sorted = [...filtered].sort(
          (a, b) => b.incomePerGuest - a.incomePerGuest
        );
        break;
      case "roi-asc":
        // Pre-calculate ROI for each facility once
        const roiCache = {};
        filtered.forEach((facility) => {
          const cacheKey = facility.type;
          if (roiCache[cacheKey] === undefined) {
            roiCache[cacheKey] = calculateROI(facility);
          }
        });

        sorted = [...filtered].sort((a, b) => {
          const roiA = roiCache[a.type] ?? Infinity;
          const roiB = roiCache[b.type] ?? Infinity;

          if (roiA === Infinity && roiB === Infinity) return 0;
          if (roiA === Infinity) return 1;
          if (roiB === Infinity) return -1;
          return roiA - roiB;
        });
        break;
      default:
        sorted = filtered;
    }

    return sorted;
  }, [
    filteredFacilityTypes,
    sortOrder,
    normalizedFacilityTypes,
    facilities,
    money,
    selectedTab,
  ]);

  // Get icon component for facility type (memoized)
  const getFacilityIcon = useCallback(
    (facilityType) => {
      // Get the facility object from lookup map
      const facility = facilityTypesMap[facilityType];

      // If we have the facility and it has an icon, return it
      if (facility && facility.icon) {
        return facility.icon;
      }

      // Otherwise use a fallback icon mapping
      const iconMap = {
        Restaurant: <Utensils className="w-5 h-5 text-orange-500" />,
        Bar: <Coffee className="w-5 h-5 text-amber-600" />,
        Spa: <Heart className="w-5 h-5 text-pink-500" />,
        Gym: <Activity className="w-5 h-5 text-red-500" />,
        Pool: <Umbrella className="w-5 h-5 text-blue-500" />,
        "Conference Room": <Users className="w-5 h-5 text-indigo-500" />,
        "Gift Shop": <Gift className="w-5 h-5 text-emerald-500" />,
        Casino: <DollarSign className="w-5 h-5 text-green-600" />,
        Nightclub: <Music className="w-5 h-5 text-purple-600" />,
        "Rooftop Garden": <Flower className="w-5 h-5 text-emerald-400" />,
        "Business Center": <Printer className="w-5 h-5 text-slate-600" />,
        "Kids Club": <Smile className="w-5 h-5 text-amber-400" />,
        "Art Gallery": <Image className="w-5 h-5 text-violet-500" />,
        "Pet Spa": <Dog className="w-5 h-5 text-amber-500" />,
        "Tennis Court": <Disc className="w-5 h-5 text-green-500" />,
        "Cigar Lounge": <Minus className="w-5 h-5 text-amber-700" />,
        "Movie Theater": <Film className="w-5 h-5 text-red-600" />,
        Library: <Book className="w-5 h-5 text-cyan-700" />,
        Lobby: <Crown className="w-5 h-5 text-amber-400" />,
        Gondola: <Ship className="w-5 h-5 text-amber-400" />,
        "Private Submarine Experience": (
          <Fish className="w-5 h-5 text-blue-600" />
        ),
        "Rooftop Helipad & Skylounge": (
          <Plane className="w-5 h-5 text-indigo-600" />
        ),
      };

      return (
        iconMap[facilityType] || <Package className="w-5 h-5 text-gray-500" />
      );
    },
    [facilityTypesMap]
  );

  // Get themed background color for facility (memoized)
  const getFacilityThemeColor = useCallback((facilityType) => {
    const colorMap = {
      Restaurant: "bg-orange-50 border-orange-200",
      Bar: "bg-amber-50 border-amber-200",
      Spa: "bg-pink-50 border-pink-200",
      Gym: "bg-red-50 border-red-200",
      Pool: "bg-blue-50 border-blue-200",
      "Conference Room": "bg-indigo-50 border-indigo-200",
      "Gift Shop": "bg-emerald-50 border-emerald-200",
      Casino: "bg-green-50 border-green-200",
      Nightclub: "bg-purple-50 border-purple-200",
      "Rooftop Garden": "bg-emerald-50 border-emerald-200",
      "Business Center": "bg-slate-50 border-slate-200",
      "Kids Club": "bg-amber-50 border-amber-200",
      "Art Gallery": "bg-violet-50 border-violet-200",
      "Pet Spa": "bg-amber-50 border-amber-200",
      "Tennis Court": "bg-green-50 border-green-200",
      "Cigar Lounge": "bg-amber-50 border-amber-200",
      "Movie Theater": "bg-red-50 border-red-200",
      Library: "bg-cyan-50 border-cyan-200",
      Lobby: "bg-amber-50 border-amber-200",
      Gondola: "bg-amber-50 border-amber-200",
      "Private Submarine Experience": "bg-blue-50 border-blue-200",
      "Rooftop Helipad & Skylounge": "bg-indigo-50 border-indigo-200",
    };

    return colorMap[facilityType] || "bg-gray-50 border-gray-200";
  }, []);

  // Calculate estimated revenue with caching
  const calculateEstimatedRevenue = useCallback(
    (facility) => {
      // Skip calculation for Lobby
      if (facility.name === "Lobby") return 0;

      // Create a cache key
      const cacheKey = `rev-${facility.id}-${facility.level}-${guests.length}-${reputation}`;

      // Check if result is already in cache
      if (calculationCacheRef.current[cacheKey] !== undefined) {
        return calculationCacheRef.current[cacheKey];
      }

      // Get facility type data from lookup map
      const facilityType = facilityTypesMap[facility.name];
      if (!facilityType) return 0;

      // More realistic usage calculation
      const baseUsageRate = 0.05 + reputation / 10;

      // Count guest types (do this only once)
      const businessGuests = guests.filter((g) => g.type === "Business").length;
      const familyGuests = guests.filter((g) => g.type === "Family").length;
      const leisureGuests = guests.filter((g) => g.type === "Leisure").length;

      // Calculate facility appeal by guest type
      let typeMultiplier = 1.0;

      // Business appeal
      if (
        [
          "Business Center",
          "Conference Room",
          "Restaurant",
          "Bar",
          "Gym",
        ].includes(facility.name)
      ) {
        typeMultiplier += (businessGuests / Math.max(1, guests.length)) * 0.5;
      }

      // Family appeal
      if (
        [
          "Pool",
          "Kids Club",
          "Restaurant",
          "Game Room",
          "Movie Theater",
        ].includes(facility.name)
      ) {
        typeMultiplier += (familyGuests / Math.max(1, guests.length)) * 0.5;
      }

      // Leisure appeal
      if (
        ["Spa", "Bar", "Pool", "Nightclub", "Casino", "Art Gallery"].includes(
          facility.name
        )
      ) {
        typeMultiplier += (leisureGuests / Math.max(1, guests.length)) * 0.5;
      }

      // Calculate usage rate with reasonable cap
      const usageRate = Math.min(0.8, baseUsageRate * typeMultiplier);

      // Apply diminishing returns for large guest counts
      const effectiveGuests =
        guests.length <= 10
          ? guests.length
          : 10 + Math.sqrt(guests.length - 10);

      // Final revenue calculation with safety check for incomePerGuest
      const incomePerGuest = facilityType.incomePerGuest || 0;
      const revenue = Math.round(
        effectiveGuests *
          usageRate *
          incomePerGuest *
          (0.8 + facility.level * 0.2)
      );

      // Save in cache
      calculationCacheRef.current[cacheKey] = revenue;

      return revenue;
    },
    [guests, reputation, facilityTypesMap]
  );

  // Calculate ROI with caching
  const calculateROI = useCallback(
    (facilityType) => {
      // Check if facilityType is valid
      if (!facilityType || !facilityType.type) return Infinity;

      const existingFacility = facilities.find(
        (f) => f.name === facilityType.type
      );

      // Create a cache key
      const cacheKey = existingFacility
        ? `roi-${facilityType.type}-upgrade-${existingFacility.level}-${guests.length}-${reputation}`
        : `roi-${facilityType.type}-new-${guests.length}-${reputation}`;

      // Check if result is already in cache
      if (calculationCacheRef.current[cacheKey] !== undefined) {
        return calculationCacheRef.current[cacheKey];
      }

      let roi;

      // Calculate ROI for existing facility upgrade
      if (existingFacility) {
        // Skip calculation for max level facilities
        if (existingFacility.level >= 5) {
          calculationCacheRef.current[cacheKey] = Infinity;
          return Infinity;
        }

        const upgradeCost = 5000 * existingFacility.level;
        const currentRevenue = calculateEstimatedRevenue(existingFacility);

        // More realistic revenue after upgrade
        const levelMultiplierNow = 0.8 + existingFacility.level * 0.2;
        const levelMultiplierAfter = 0.8 + (existingFacility.level + 1) * 0.2;
        const revenueFactor = levelMultiplierAfter / levelMultiplierNow;

        const estimatedNewRevenue = Math.round(currentRevenue * revenueFactor);
        const revenueIncrease = estimatedNewRevenue - currentRevenue;

        // Consider maintenance increase
        const maintenanceIncrease = existingFacility.upkeep * 0.1;
        const netDailyBenefit = revenueIncrease - maintenanceIncrease;

        roi =
          netDailyBenefit > 0
            ? Math.ceil(upgradeCost / netDailyBenefit)
            : Infinity;
      }
      // Calculate ROI for new facility
      else {
        // Basic usage calculation
        const baseUsageRate = 0.05 + reputation / 10;

        // Count guest types (do this only once per call)
        const businessGuests = guests.filter(
          (g) => g.type === "Business"
        ).length;
        const familyGuests = guests.filter((g) => g.type === "Family").length;
        const leisureGuests = guests.filter((g) => g.type === "Leisure").length;

        // Calculate type appeal
        let typeMultiplier = 1.0;

        // Business appeal
        if (
          [
            "Business Center",
            "Conference Room",
            "Restaurant",
            "Bar",
            "Gym",
          ].includes(facilityType.type)
        ) {
          typeMultiplier += (businessGuests / Math.max(1, guests.length)) * 0.5;
        }

        // Family appeal
        if (
          [
            "Pool",
            "Kids Club",
            "Restaurant",
            "Game Room",
            "Movie Theater",
          ].includes(facilityType.type)
        ) {
          typeMultiplier += (familyGuests / Math.max(1, guests.length)) * 0.5;
        }

        // Leisure appeal
        if (
          ["Spa", "Bar", "Pool", "Nightclub", "Casino", "Art Gallery"].includes(
            facilityType.type
          )
        ) {
          typeMultiplier += (leisureGuests / Math.max(1, guests.length)) * 0.5;
        }

        // New facility novelty bonus
        const noveltyFactor = 1.2;

        // Apply diminishing returns for large guest counts
        const effectiveGuests =
          guests.length <= 10
            ? guests.length
            : 10 + Math.sqrt(guests.length - 10);

        // Final usage rate with reasonable cap
        const usageRate = Math.min(
          0.75,
          baseUsageRate * typeMultiplier * noveltyFactor
        );

        // Calculate estimated revenue with safety checks for null/undefined
        const incomePerGuest = facilityType.incomePerGuest || 0;
        const upkeep = facilityType.upkeep || 0;
        const baseCost = facilityType.baseCost || 10000;

        const estimatedRevenue = Math.round(
          effectiveGuests * usageRate * incomePerGuest
        );

        const netRevenue = estimatedRevenue - upkeep;

        // Factor in ramp-up time
        const rampUpFactor = 1.5;
        const adjustedNetRevenue = netRevenue / rampUpFactor;

        roi =
          adjustedNetRevenue > 0
            ? Math.ceil(baseCost / adjustedNetRevenue)
            : Infinity;
      }

      // Save in cache
      calculationCacheRef.current[cacheKey] = roi;

      return roi;
    },
    [
      facilities,
      guests,
      reputation,
      calculateEstimatedRevenue,
      facilityTypesMap,
    ]
  );

  // Skip rendering when not on facilities tab
  if (selectedTab !== "facilities") {
    return null;
  }

  // Calculate total facilities revenue and upkeep only once per render
  const totalFacilitiesRevenue = facilities.reduce(
    (sum, facility) => sum + calculateEstimatedRevenue(facility),
    0
  );

  const totalFacilitiesUpkeep = facilities.reduce(
    (sum, facility) => sum + facility.upkeep,
    0
  );

  const netFacilitiesProfit = totalFacilitiesRevenue - totalFacilitiesUpkeep;

  // Get all facilities for correct count in loading indicator
  const allFacilities = getFilteredAndSortedFacilities();
  const totalFacilityCount = allFacilities.length;

  return (
    <div>
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <h2 className="text-xl font-bold flex items-center">
          <Construction className="w-6 h-6 mr-2 text-blue-600" />
          Facilities Management
          {recentlyBuilt.length > 0 && (
            <Sparkles className="w-5 h-5 ml-2 text-amber-500 animate-pulse" />
          )}
        </h2>

        <div className="flex space-x-2">
          <select
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
            value={filteredFacilityTypes}
            onChange={(e) => setFilteredFacilityTypes(e.target.value)}
          >
            <option value="all">All Facilities</option>
            <option value="built">Built Only</option>
            <option value="unbuilt">Unbuilt Only</option>
            <option value="affordable">Affordable</option>
          </select>

          <select
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="default">Default Order</option>
            <option value="cost-asc">Cost: Low to High</option>
            <option value="cost-desc">Cost: High to Low</option>
            <option value="income-asc">Income: Low to High</option>
            <option value="income-desc">Income: High to Low</option>
            <option value="roi-asc">Best ROI First</option>
          </select>
        </div>
      </div>

      <div
        id="facility-cards"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6"
      >
        {visibleFacilities.map((facilityType) => {
          const existingFacility = facilities.find(
            (f) => f.name === facilityType.type
          );
          // Pre-calculate values once per card
          const roi = calculateROI(facilityType);
          const isRecentlyBuiltFacility = recentlyBuilt.includes(
            facilityType.type
          );
          const themeColors = getFacilityThemeColor(facilityType.type);

          return (
            <div
              key={facilityType.type}
              className={`border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${
                isRecentlyBuiltFacility ? "animate-pulse" : ""
              } ${existingFacility ? "border-2" : "border"} ${
                existingFacility ? themeColors : "border-gray-200"
              }`}
            >
              <div
                className={`p-3 ${
                  existingFacility ? themeColors : "bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold flex items-center">
                    {getFacilityIcon(facilityType.type)}
                    <span className="ml-2">{facilityType.type}</span>

                    {typeof facilityType.incomePerGuest === "number" &&
                      facilityType.incomePerGuest > 50 && (
                        <BadgeDollarSign
                          className="w-4 h-4 ml-2 text-green-500"
                          title="High income potential"
                        />
                      )}
                    {roi < 20 && roi !== Infinity && (
                      <TrendingUp
                        className="w-4 h-4 ml-1 text-green-600"
                        title="Excellent ROI"
                      />
                    )}
                  </h3>

                  {existingFacility && (
                    <div className="flex items-center">
                      {existingFacility.level >= 5 && (
                        <Crown
                          className="w-4 h-4 text-amber-500 mr-1"
                          title="Maximum Level"
                        />
                      )}
                      <span
                        className={`text-xs px-2 py-1 rounded-full
                        ${
                          existingFacility.level >= 5
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        Level {existingFacility.level}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3">
                <p className="text-sm mb-3">{facilityType.description}</p>

                <div className="text-xs mb-3 grid grid-cols-2 gap-x-2 gap-y-1">
                  <div className="flex items-center">
                    <Construction className="w-3.5 h-3.5 mr-1 text-gray-500" />
                    <span>Cost: ${facilityType.baseCost.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center">
                    <BadgeDollarSign className="w-3.5 h-3.5 mr-1 text-green-500" />
                    <span>Income: ~${facilityType.incomePerGuest}/guest</span>
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="w-3.5 h-3.5 mr-1 text-red-500" />
                    <span>Upkeep: ${facilityType.upkeep}/day</span>
                  </div>
                  <div className="flex items-center">
                    <Award className="w-3.5 h-3.5 mr-1 text-blue-500" />
                    <span>
                      Quality Boost: +
                      {Math.ceil((facilityType.incomePerGuest || 0) / 15)}
                    </span>
                  </div>

                  {facilityType.specialFeature && (
                    <div className="col-span-2 mt-1 bg-amber-50 p-1 rounded-md text-amber-800 flex items-center">
                      <Zap className="w-3.5 h-3.5 mr-1 text-amber-500 flex-shrink-0" />
                      <span>{facilityType.specialFeature}</span>
                    </div>
                  )}

                  {showAdvancedStats && (
                    <div className="col-span-2 mt-1 pt-1 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="flex items-center">
                          <TrendingUp className="w-3.5 h-3.5 mr-1 text-blue-600" />
                          Estimated ROI:
                        </span>
                        <span
                          className={`font-medium ${
                            roi < 20
                              ? "text-green-600"
                              : roi < 40
                              ? "text-emerald-600"
                              : roi < 60
                              ? "text-amber-600"
                              : "text-red-600"
                          }`}
                        >
                          {roi !== Infinity ? `${roi} days` : "N/A"}
                        </span>
                      </div>

                      {existingFacility && (
                        <div className="flex justify-between items-center mt-1">
                          <span className="flex items-center">
                            <Zap className="w-3.5 h-3.5 mr-1 text-amber-500" />
                            Daily revenue:
                          </span>
                          <span className="text-green-600 font-medium">
                            ${calculateEstimatedRevenue(existingFacility)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!existingFacility &&
                  facilityType.requiredStaff &&
                  facilityType.requiredStaff.length > 0 && (
                    <div className="mb-2 text-xs text-red-600">
                      Requires staff: {facilityType.requiredStaff.join(", ")}
                    </div>
                  )}

                {existingFacility ? (
                  <button
                    onClick={() => upgradeFacility(existingFacility.id)}
                    disabled={
                      existingFacility.level >= 5 ||
                      money < 5000 * existingFacility.level
                    }
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center ${
                      existingFacility.level >= 5
                        ? "bg-amber-100 text-amber-800"
                        : money < 5000 * existingFacility.level
                        ? "bg-gray-200 text-gray-500"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    {existingFacility.level >= 5 ? (
                      <>
                        <Crown className="w-4 h-4 mr-1.5" />
                        Maximum Level Reached
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        Upgrade to Level {existingFacility.level + 1} ($
                        {(5000 * existingFacility.level).toLocaleString()})
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      buildFacility(facilityType.type);
                      setRecentlyBuilt((prev) => [...prev, facilityType.type]);
                    }}
                    disabled={
                      money < facilityType.baseCost ||
                      (facilityType.requiredStaff &&
                        facilityType.requiredStaff.length > 0 &&
                        !facilityType.requiredStaff.every((role) =>
                          staff.some((s) => s.role === role)
                        ))
                    }
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center ${
                      money < facilityType.baseCost ||
                      (facilityType.requiredStaff &&
                        facilityType.requiredStaff.length > 0 &&
                        !facilityType.requiredStaff.every((role) =>
                          staff.some((s) => s.role === role)
                        ))
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    <Construction className="w-4 h-4 mr-1.5" />
                    Build ${facilityType.baseCost.toLocaleString()}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Improved loading indicator with progress */}
        {isChunking && (
          <div className="col-span-1 sm:col-span-2 flex flex-col justify-center items-center p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <span className="text-blue-600 text-sm">
              Loading more facilities... {chunksLoaded} of {totalFacilityCount}{" "}
              loaded
            </span>
            <p className="text-xs text-blue-500 mt-1">
              {chunksLoaded > 0
                ? "Scroll down to see facilities already loaded"
                : "Loading first facilities..."}
            </p>
          </div>
        )}
      </div>

      {/* Toggle for advanced stats */}
      <button
        onClick={() => setShowAdvancedStats(!showAdvancedStats)}
        className="mb-4 text-sm flex items-center text-blue-600 hover:text-blue-800"
      >
        {showAdvancedStats ? (
          <ChevronUp className="w-4 h-4 mr-1" />
        ) : (
          <ChevronDown className="w-4 h-4 mr-1" />
        )}
        {showAdvancedStats
          ? "Hide Advanced Statistics"
          : "Show Advanced Statistics"}
      </button>

      {/* Current Facilities Stats */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4">
        <h3 className="font-bold mb-3 flex items-center">
          <Package className="w-5 h-5 mr-2 text-blue-600" />
          Current Facilities
        </h3>

        {facilities.length <= 1 ? (
          <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border border-gray-200">
            <Construction className="w-12 h-12 text-gray-400 mb-2" />
            <p className="text-gray-500 italic text-center">
              You only have the basic lobby. Build more facilities to attract
              guests and boost your hotel's reputation!
            </p>
            <button
              onClick={() => {
                document.getElementById("facility-cards")?.scrollIntoView({
                  behavior: "smooth",
                });
              }}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              Browse Available Facilities
            </button>
          </div>
        ) : (
          <div className="overflow-auto max-h-64">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Facility</th>
                  <th className="p-2 text-left">Level</th>
                  <th className="p-2 text-left">Daily Upkeep</th>
                  <th className="p-2 text-left">Est. Revenue</th>
                  <th className="p-2 text-center">Profit</th>
                </tr>
              </thead>
              <tbody>
                {/* Only render facilities in view to prevent memory issues */}
                {facilities.slice(0, 50).map((facility) => {
                  // Pre-calculate values once
                  const estRevenue = calculateEstimatedRevenue(facility);
                  const profit = estRevenue - facility.upkeep;
                  const isRecentlyBuilt = recentlyBuilt.includes(facility.name);

                  return (
                    <tr
                      key={facility.id}
                      className={`border-b hover:bg-gray-50 ${
                        isRecentlyBuilt ? "animate-pulse bg-green-50" : ""
                      }`}
                    >
                      <td className="p-2 flex items-center">
                        {getFacilityIcon(facility.name)}
                        <span className="ml-2">{facility.name}</span>
                        {facility.level >= 5 && (
                          <Crown
                            className="w-3.5 h-3.5 ml-1.5 text-amber-500"
                            title="Maximum Level"
                          />
                        )}
                      </td>
                      <td className="p-2">{renderStars(facility.level)}</td>
                      <td className="p-2 text-red-600">
                        -${facility.upkeep}/day
                      </td>
                      <td className="p-2 text-green-600">+${estRevenue}/day</td>
                      <td className="p-2 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            profit > 0
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {profit >= 0 ? "+" : ""}
                          {profit}/day
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Summary row - using pre-calculated totals */}
                <tr className="bg-gray-100 font-semibold">
                  <td className="p-2">Total</td>
                  <td className="p-2">-</td>
                  <td className="p-2 text-red-600">
                    -${totalFacilitiesUpkeep}/day
                  </td>
                  <td className="p-2 text-green-600">
                    +${totalFacilitiesRevenue}/day
                  </td>
                  <td className="p-2 text-center">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        netFacilitiesProfit > 0
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {netFacilitiesProfit >= 0 ? "+" : ""}$
                      {netFacilitiesProfit}/day
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
          <h4 className="font-bold mb-2 flex items-center text-blue-800">
            <Info className="w-4 h-4 mr-2" /> Facility Strategy
          </h4>
          <ul className="text-sm space-y-1 text-blue-900">
            <li className="flex items-start">
              <PlusCircle className="w-3 h-3 mt-1 mr-2 flex-shrink-0" />
              <span>
                Facilities provide both income and guest satisfaction boosts
              </span>
            </li>
            <li className="flex items-start">
              <PlusCircle className="w-3 h-3 mt-1 mr-2 flex-shrink-0" />
              <span>
                Higher level facilities generate more revenue but cost more to
                maintain
              </span>
            </li>
            <li className="flex items-start">
              <PlusCircle className="w-3 h-3 mt-1 mr-2 flex-shrink-0" />
              <span>Different guest types prefer different facilities</span>
            </li>
          </ul>
        </div>

        <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
          <h4 className="font-bold mb-2 flex items-center text-amber-800">
            <AlertCircle className="w-4 h-4 mr-2" /> Did You Know?
          </h4>
          <ul className="text-sm space-y-1 text-amber-900">
            <li className="flex items-start">
              <Zap className="w-3 h-3 mt-1 mr-2 flex-shrink-0" />
              <span>
                Business travelers prefer Restaurants, Bars and Business Centers
              </span>
            </li>
            <li className="flex items-start">
              <Zap className="w-3 h-3 mt-1 mr-2 flex-shrink-0" />
              <span>Families enjoy Pools, Restaurants and Kids Clubs</span>
            </li>
            <li className="flex items-start">
              <Zap className="w-3 h-3 mt-1 mr-2 flex-shrink-0" />
              <span>
                Leisure travelers are drawn to Spas, Bars and Entertainment
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FacilityManagementTab;
